import { Component, computed, input, numberAttribute } from '@angular/core';
import { EarningsStatDataSet, epsCurrencySymbol } from '../earnings-data';

interface EpsQuarter {
  label: string;
  expected: number | null;
  actual: number | null;
  upcoming?: boolean;
  upcomingDate?: string;
}

@Component({
  selector: 'app-eps-chart',
  imports: [],
  standalone: true,
  templateUrl: './eps-chart.html',
  styleUrl: './eps-chart.scss',
  // Only EpsWidget sets [v3]; EarningsOverview never does, so its V2 chart is unaffected.
  host: { '[class.v3]': 'v3()', '[class.animate-in]': 'animateIn()' },
})
export class EpsChart {
  readonly data = input<EarningsStatDataSet | null>(null);
  readonly v3 = input(false);
  // Mount animation (V3 pages only — the /eps widget and V2 don't set it).
  readonly animateIn = input(false);
  // Hides the [v3] "Earnings per Share" title row for a consumer with its own title elsewhere.
  readonly legend = input(true);
  // Y-axis tick count. Defaults to 5 and is clamped to at least 2 to keep the axis math safe.
  // numberAttribute also handles values passed as static HTML attributes.
  readonly steps = input(5, {
    transform: (value: unknown) => {
      const n = numberAttribute(value, NaN);
      return Number.isFinite(n) ? Math.max(Math.trunc(n), 2) : 5;
    },
  });

  readonly quarters = computed((): EpsQuarter[] => {
    const d = this.data();
    if (!d) return [];
    return d.quarterly.map((p) => ({
      label: this.formatQuarterLabel(String(p.label)),
      expected: p.expected,
      actual: p.reported,
      upcoming: p.reported === null,
    }));
  });

  readonly currency = computed(() => {
    const d = this.data();
    if (!d) return 'USD';
    return d.quarterly.find((p) => p.currency)?.currency ?? 'USD';
  });

  // V3 legend's currency unit — same helper V3EarningsDesktopContent's own title/subtitle uses.
  readonly currencySymbol = computed(() => epsCurrencySymbol(this.data()));

  readonly yAxis = computed(() => {
    const n = this.steps();
    const allValues = this.quarters().flatMap((q) => {
      const vals: number[] = [];
      if (q.expected !== null) vals.push(q.expected);
      if (q.actual !== null) vals.push(q.actual);
      return vals;
    });
    if (allValues.length === 0) {
      return Array.from({ length: n }, (_, i) => (n - 1 - i) / (n - 1));
    }

    const max = Math.max(...allValues);
    const min = Math.min(...allValues);
    // Divisor is (n-2), not (n-1): leaves one tick's headroom above max after floor-aligning
    // the bottom. Floored at 1 so a 3-tick axis still works.
    const divisor = Math.max(n - 2, 1);
    const raw = max === min ? Math.abs(max) || 1 : (max - min) / divisor;
    const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
    const normalized = raw / magnitude;
    const nice =
      normalized <= 1
        ? 1
        : normalized <= 2
          ? 2
          : normalized <= 2.5
            ? 2.5
            : normalized <= 5
              ? 5
              : 10;
    const step = nice * magnitude;
    const bottom = Math.floor(min / step) * step;

    return Array.from(
      { length: n },
      (_, i) => Math.round((bottom + (n - 1 - i) * step) * 100) / 100,
    );
  });

  readonly yMin = computed(() => Math.min(...this.yAxis()));
  readonly yMax = computed(() => Math.max(...this.yAxis()));

  barHeight(value: number): number {
    const range = this.yMax() - this.yMin();
    if (range === 0) return 0;
    return ((value - this.yMin()) / range) * 100;
  }

  // Connector lines (V3 only): solid between reported quarters, dashed into the upcoming
  // dot. A quarter with no dot breaks the chain.
  readonly lineSegments = computed(() => {
    const qs = this.quarters();
    const n = qs.length;
    if (n < 2) return [];

    const dotKind = (q: EpsQuarter): 'reported' | 'upcoming' | 'none' => {
      if (!q.upcoming) return 'reported';
      if (q.expected !== null) return 'upcoming';
      return 'none';
    };

    const point = (i: number, kind: 'reported' | 'upcoming') => {
      const q = qs[i];
      const value = kind === 'reported' ? q.actual! : q.expected!;
      return { x: ((i + 0.5) / n) * 100, y: 100 - this.barHeight(value) };
    };

    const segments: { x1: number; y1: number; x2: number; y2: number; dashed: boolean }[] = [];
    for (let i = 0; i < n - 1; i++) {
      const kindA = dotKind(qs[i]);
      const kindB = dotKind(qs[i + 1]);
      if (kindA === 'none' || kindB === 'none') continue;
      const a = point(i, kindA);
      const b = point(i + 1, kindB);
      segments.push({
        x1: a.x,
        y1: a.y,
        x2: b.x,
        y2: b.y,
        dashed: kindA === 'upcoming' || kindB === 'upcoming',
      });
    }
    return segments;
  });

  // True only when both values are present and actual beats expected
  isBeat(q: EpsQuarter): boolean {
    return q.actual !== null && q.expected !== null && q.actual >= q.expected;
  }

  // Whether beat/miss comparison is possible
  isComparable(q: EpsQuarter): boolean {
    return q.actual !== null && q.expected !== null;
  }

  delta(q: EpsQuarter): string {
    if (q.actual === null || q.expected === null) return '';
    const diff = q.actual - q.expected;
    const sign = diff >= 0 ? '+' : '';
    const formatted = diff.toFixed(2).replace('.', ',');
    return this.formatWithCurrency(`${sign}${formatted}`);
  }

  private formatWithCurrency(signedValue: string): string {
    const currency = this.currency();
    if (currency === 'EUR') return `${signedValue}€`;
    if (currency === 'USD') return `${signedValue[0]}$${signedValue.slice(1)}`;
    return `${signedValue} ${currency}`;
  }

  formatExpected(value: number): string {
    return value.toFixed(2).replace('.', ',');
  }

  formatTick(value: number): string {
    return value.toFixed(1).replace('.', ',');
  }

  private formatQuarterLabel(label: string): string {
    const match = label.match(/^(Q\d)\s(\d{4})$/);
    if (match) return `${match[1]}'${match[2].slice(-2)}`;
    return label;
  }
}
